import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './RegistrationFees.css';

const RegistrationForm = () => {
    const [fees, setFees] = useState([
        { id: 1, category: 'Authors', local: '', international: '' },
        { id: 2, category: 'Authors (Student)', local: '', international: '' },
        { id: 3, category: 'Participants', local: '', international: '' }
    ]);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (id, field, value) => {
        setFees(prevFees => 
            prevFees.map(fee => 
                fee.id === id ? { ...fee, [field]: value } : fee
            )
        );
    };

    const validateInputs = () => {
        for (const fee of fees) {
            if (!fee.local || !fee.international) {
                return false;
            }
            if (parseFloat(fee.local) < 0 || parseFloat(fee.international) < 0) {
                return false;
            }
        }
        return true;
    };

    const save = async () => {
        // Validation check
        if (!validateInputs()) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Information',
                text: 'Please fill in all fee fields with valid positive amounts.',
                confirmButtonColor: '#5e71f6',
            });
            return;
        }

        setLoading(true);
        try {
            const data = fees.map(fee => ({
                category: fee.category,
                local: parseFloat(fee.local),
                international: parseFloat(fee.international),
            }));

            await axios.put(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/registration-fee`, data, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Registration fees updated successfully!',
                confirmButtonColor: '#5e71f6',
            });

        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed!',
                text: error.response?.data?.message || 'Error saving data! Please try again.',
                confirmButtonColor: '#ef4444',
            });
            console.error('Error saving data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-fees-wrapper">
            <div className="registration-fees-container">
                {/* Header Section */}
                <div className="header-section">
                    <h1 className="page-title">Registration Fees</h1>
                    <p className="page-subtitle">Set conference registration fees for different participant categories</p>
                </div>

                {/* Fees Management Card */}
                <div className="fees-management-card">
                    <div className="card-header">
                        <h2 className="card-title">
                            <svg className="title-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Fee Categories
                        </h2>
                        <div className="categories-count">
                            {fees.length} Categories
                        </div>
                    </div>

                    <div className="fees-list">
                        {fees.map((fee, index) => (
                            <div key={fee.id} className="fee-item">
                                <div className="fee-header">
                                    <div className="category-badge">
                                        <svg className="category-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        {fee.category}
                                    </div>
                                </div>

                                <div className="fee-fields">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Local Fee (BDT) <span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={fee.local}
                                            onChange={(e) => handleInputChange(fee.id, 'local', e.target.value)}
                                            placeholder="Enter amount in BDT"
                                            className="form-input"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            International Fee (USD) <span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={fee.international}
                                            onChange={(e) => handleInputChange(fee.id, 'international', e.target.value)}
                                            placeholder="Enter amount in USD"
                                            className="form-input"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="card-actions">
                        <button 
                            type="button"
                            onClick={save}
                            disabled={loading}
                            className="save-fees-btn"
                        >
                            {loading ? (
                                <>
                                    <div className="loading-spinner-small"></div>
                                    Saving Fees...
                                </>
                            ) : (
                                <>
                                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                    </svg>
                                    Save Fees
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationForm;
